/**
 * The on-chain profile rules the /u flows show before a confirmation (pure, tested). Measured on a BSC mainnet fork
 * (evidence/stage-profile-merge-20261003/fork-sequences.txt), contracts unchanged:
 * - buyNFTs pulls priceInCbon × count, minus referralDiscount (1000 bp = 10 %) when the PAYER is already referred on
 *   CadinuReferral (any referral argument then) or passes a referrer that is not itself; a self-referral reverts.
 * - The locked NFT carries LEVEL_POINT × level points: they leave with it (pause, or a lower level) and come back with it.
 */
const ADDR = /^0x[0-9a-fA-F]{40}$/
const ZERO = /^0x0{40}$/i
export const isAddr = (a?: string | null): a is `0x${string}` => typeof a === 'string' && ADDR.test(a) && !ZERO.test(a)

export type DiscountSource = 'referred' | 'referrer' | null

/**
 * The referral argument to send and whether the 10 % applies. `referredTo` = CadinuReferral.referredTo(payer) (bound for good
 * at the payer's first referred purchase); `referrer` = the address typed or prefilled. The legacy check is kept: the
 * referrer can be neither the payer nor the recipient.
 */
export function referralTerms(i: { referredTo?: string | null; useReferrer: boolean; referrer?: string | null; payer?: string | null; recipient?: string | null }): {
  arg: `0x${string}`
  source: DiscountSource
  problem: 'self' | 'invalid' | null
} {
  const zero = '0x0000000000000000000000000000000000000000' as const
  if (isAddr(i.referredTo)) return { arg: i.referredTo, source: 'referred', problem: null }
  if (!i.useReferrer || !i.referrer) return { arg: zero, source: null, problem: null }
  if (!isAddr(i.referrer)) return { arg: zero, source: null, problem: 'invalid' }
  const r = i.referrer.toLowerCase()
  if ([i.payer, i.recipient].filter(Boolean).some((x) => String(x).toLowerCase() === r)) return { arg: zero, source: null, problem: 'self' }
  return { arg: i.referrer, source: 'referrer', problem: null }
}

/** CBON the NFT contract pulls for `count` NFTs: price × count, less the referral discount in basis points when it applies. */
export function buyTotal(price: bigint, count: bigint, discountBp: bigint, discounted: boolean): { gross: bigint; discount: bigint; total: bigint } {
  const gross = price * count
  const total = discounted ? (gross * (10_000n - discountBp)) / 10_000n : gross
  return { gross, discount: gross - total, total }
}

/** Points change of a profile action: level points of the NFT that is locked after it minus those of the one before. */
export function pointsDelta(levelPoint: bigint, before: number, after: number): bigint {
  return levelPoint * BigInt(after) - levelPoint * BigInt(before)
}

/** "+1,000 points" / "−2,000 points" / "no change to points". */
export function pointsText(delta: bigint): string {
  if (delta === 0n) return 'no change to your points'
  const n = Number(delta < 0n ? -delta : delta).toLocaleString('en-US')
  return delta > 0n ? `+${n} points` : `−${n} points`
}

/** A whole-number quantity ≥ 1 from a text field, capped (remaining supply), else null. */
export function parseCount(s: string, max?: bigint): bigint | null {
  if (!/^\d{1,6}$/.test(s.trim())) return null
  const n = BigInt(s.trim())
  if (n < 1n) return null
  if (max !== undefined && n > max) return null
  return n
}
